/**
 * manage/manage-session — the credential vault, the exchange, and the two-stage
 * authority state machine.
 *
 * WHAT THIS FILE IS FOR. Docket 0029 §"The token in the path" and §"Two levels
 * of authority" are protocol, not screen work: one exchange, tokens that live in
 * memory and nowhere else, a step-up that is triggered by the SERVER's refusal
 * rather than by the page guessing, and mutation envelopes keyed per user intent.
 * All of that lives here so no component holds a credential or decides an
 * authority question.
 *
 * ⛔ THE VAULT IS A MODULE CLOSURE, NOT REACT STATE. Session and elevated tokens
 * are never put in `useState`, never in a `data-*` attribute, never in a query
 * string, never logged, never in `localStorage`/`sessionStorage`. React state is
 * inspectable in a devtools panel and serialisable by any error reporter that
 * walks the tree; a module-local `let` is neither. Components receive BOOLEANS
 * and DERIVED FACTS (`elevated`, `scopes`) — never the strings.
 *
 * ⛔ THE PATH SEGMENT STAYS IN THE ADDRESS BAR, AND IS STILL NOT PROOF OF ANYONE.
 * Since JAI-815 (addendum 0038) the segment is the BOOKING ID rather than an
 * opaque token: it is printed in the customer's own email, on their own device.
 * It used to be erased within one paint of the exchange (`history.replaceState`)
 * to keep it out of a `Referer`, a bookmark and a screen-share. That erase is
 * GONE as of 0049, for two reasons and one cost:
 *   - `Referer` is closed by a header we serve on this surface
 *     (`referrer-policy: no-referrer`, alongside `cache-control: no-store`,
 *     `frame-ancestors 'none'` and a `connect-src 'self'` CSP). A server-enforced
 *     rule is the right place for it, and the client erase added nothing to it.
 *   - a bookmark or a history entry is not a leak we are preventing: the address
 *     bar is strictly less exposed than the inbox the link arrived in.
 *   - the cost was every reload, every "open in a new tab", every restored
 *     session and every hand-off at a desk — each of them landed on the no-link
 *     screen with the booking one keystroke out of reach.
 * Nothing else about the vault changed: session and elevation tokens are still
 * memory-only, and the segment is still never treated as proof of identity —
 * every high-impact call goes through `run()`, which honours the server's
 * `403 verificationRequired`. The segment is NOT put into `sessionStorage`
 * either; a re-exchange after rotation reads it from the vault, and the URL is
 * the durable copy it is seeded from.
 *
 * ⚠️ ONE CONTRACT SEAM IS MISSING AND THE PAGE COMPENSATES RATHER THAN PRETENDS.
 * Docket 0029 §"One response header the page must honour" requires reading
 * `X-Manage-Session-Refresh` off the reschedule response. The typed client's
 * manage wrappers return the PARSED BODY — `apiRaw` never exposes `Response`
 * headers — so a page built on the typed client (which is the rule, not a
 * preference) cannot see that header at all. Rather than open a second, untyped
 * transport, this module:
 *   1. renders the `view` the mutation returned (which the docket also requires),
 *      so no call is issued in the window where the old token is already dead;
 *   2. classifies the next `401 sessionExpired` / `sessionRequired` as
 *      ROTATION rather than death — phase `'stale'` — and offers a one-tap
 *      re-exchange from the segment held in memory, which is durable exactly
 *      because a booking-id link is stable for the life of the booking (0038).
 * That is honest and recoverable, but it is NOT the header being honoured. The
 * ask is in the reply: either surface the refreshed token in the mutation BODY,
 * or give the manage wrappers a header-observing seam.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { isApiError, manageSurface } from "@v0y4ge/api-client";
import type { ManageElevationScope, ManageViewDto } from "@v0y4ge/api-contracts";

/* ── the vault ───────────────────────────────────────────────────────── */

/** The shape the typed client's manage wrappers take. Built per call, never stored. */
interface Credentials {
  sessionToken: string;
  elevationToken?: string;
}

const vault = {
  /**
   * The path segment (the booking id), as the customer opened it. Held here so a
   * re-exchange after a rotation needs no second read; the URL keeps its own
   * copy, which is what makes a reload work.
   */
  segment: "",
  session: "",
  elevation: "",
  scopes: [] as ManageElevationScope[],
};

function credentials(): Credentials {
  return vault.elevation
    ? { sessionToken: vault.session, elevationToken: vault.elevation }
    : { sessionToken: vault.session };
}

/** Forget everything. Called when the link is dead and when the tab unloads. */
function clearVault(): void {
  vault.segment = "";
  vault.session = "";
  vault.elevation = "";
  vault.scopes = [];
}

/**
 * The single path segment, URL-decoded.
 *
 * A segment carrying a DOT is the shell itself (`/manage.html` on the dev
 * server, any asset path that reaches this entry) and is not a booking id — the
 * same rule `portal-main.tsx` applies to its slug, for the same reason: getting
 * it wrong turns a working dev server into a dead link.
 */
export function readSegment(): string {
  if (typeof window === "undefined") return "";
  const first = window.location.pathname.split("/").filter(Boolean)[0];
  if (first === undefined) return "";
  let decoded = first;
  try {
    decoded = decodeURIComponent(first);
  } catch {
    return "";
  }
  return decoded.includes(".") ? "" : decoded;
}

/* ── failures ────────────────────────────────────────────────────────── */

/**
 * The finished states of docket 0029 §"Failure paths", narrowed by 0038: for a
 * booking-id link an unknown id, another tenant's id and a garbage string all
 * answer the same `401 tokenInvalid`. `expired` and `revoked` survive for links
 * mailed before the cutover; `unreachable` is ours, not the link's.
 */
export type ManageFailure = "no-link" | "invalid" | "expired" | "revoked" | "unreachable";

function classify(error: unknown): ManageFailure {
  if (!isApiError(error)) return "unreachable";
  switch (error.code) {
    case "tokenExpired":
      return "expired";
    case "tokenRevoked":
      return "revoked";
    case "tokenInvalid":
    case "tokenSuperseded":
      return "invalid";
    default:
      // A 5xx, a network failure or an unregistered code is OUR problem, and
      // saying "this link is no longer valid" for it would send a customer to
      // support over a warming isolate.
      return "unreachable";
  }
}

/** Thrown by `run()` when the visitor dismissed the step-up. Never surfaced. */
export class StepUpAbandoned extends Error {
  constructor() {
    super("step-up abandoned");
    this.name = "StepUpAbandoned";
  }
}

/** Thrown by `run()` when the session died mid-flow; the page moves to `'stale'`. */
export class SessionLost extends Error {
  constructor() {
    super("manage session lost");
    this.name = "SessionLost";
  }
}

export function isAbandoned(error: unknown): boolean {
  return error instanceof StepUpAbandoned || error instanceof SessionLost;
}

/* ── the step-up gate ────────────────────────────────────────────────── */

export interface StepUpRequest {
  /** The scope the SERVER named in its refusal — never one the page assumed. */
  scope: ManageElevationScope;
  /** Populated by `requestCode()`; the page shows only this. */
  maskedTarget: string;
  channel: "email" | "sms" | null;
  retryAfterSeconds: number;
  sending: boolean;
  /** A catalog key, resolved by the caller. Never an envelope `message`. */
  errorKey: string | null;
}

/* ── phases ──────────────────────────────────────────────────────────── */

export type ManagePhase = "exchanging" | "ready" | "stale" | "dead";

export interface ManageSessionApi {
  phase: ManagePhase;
  failure: ManageFailure | null;
  view: ManageViewDto | null;
  /** 0029: the exchange SUCCEEDS and returns a read-only tombstone. */
  superseded: boolean;
  elevated: boolean;
  scopes: readonly ManageElevationScope[];
  /** The pending step-up, or null. Rendered by `StepUpDialog`. */
  stepUp: StepUpRequest | null;
  /** Re-run the exchange from the in-memory segment (rotation / stale session). */
  reopen: () => void;
  /** Replace the rendered view with the one a mutation returned. */
  applyView: (view: ManageViewDto) => void;
  /** Re-read the masked view from the server (never after a mutation). */
  reload: () => Promise<void>;
  /**
   * Run one operation with the credentials it needs, honouring the server's
   * refusals: a `403 verificationRequired` opens the step-up for the scope the
   * server named and retries ONCE; a dead session moves the page to `'stale'`.
   */
  run: <T>(fn: (credentials: Credentials) => Promise<T>) => Promise<T>;
  /** Step-up dialog wiring. */
  requestCode: (channel?: "email" | "sms") => Promise<void>;
  submitCode: (code: string) => Promise<void>;
  abandonStepUp: () => void;
  /** After elevation: swap the masked view for the full one (scope `pii:read`). */
  revealFull: () => Promise<void>;
}

export function useManageSession(): ManageSessionApi {
  const [phase, setPhase] = useState<ManagePhase>("exchanging");
  const [failure, setFailure] = useState<ManageFailure | null>(null);
  const [view, setView] = useState<ManageViewDto | null>(null);
  const [superseded, setSuperseded] = useState(false);
  const [elevated, setElevated] = useState(false);
  const [scopes, setScopes] = useState<readonly ManageElevationScope[]>([]);
  const [stepUp, setStepUp] = useState<StepUpRequest | null>(null);

  /** Resolvers of the promise `run()` parks on while the step-up is on screen. */
  const gate = useRef<{ promise: Promise<void>; resolve: () => void; reject: (error: unknown) => void } | null>(null);

  const exchange = useCallback(async (segment: string) => {
    setPhase("exchanging");
    setFailure(null);
    try {
      const response = await manageSurface.createManageSession({ token: segment });
      const data = response.data;
      vault.session = data.session_token;
      // A change of generation invalidates any elevation we were holding.
      vault.elevation = "";
      vault.scopes = [];
      setElevated(false);
      setScopes([]);
      setSuperseded(data.superseded === true);
      setView(data.view);
      setPhase("ready");
    } catch (error) {
      clearVault();
      setView(null);
      setFailure(classify(error));
      setPhase("dead");
    }
    // The URL is left exactly as the customer opened it, whatever the exchange
    // answered — including on a dead link, where the id in the address bar is
    // what lets them re-try, forward it, or read it out to support.
  }, []);

  useEffect(() => {
    const segment = readSegment();
    if (!segment) {
      setFailure("no-link");
      setPhase("dead");
      return;
    }
    vault.segment = segment;
    void exchange(segment);
  }, [exchange]);

  useEffect(() => {
    const forget = () => clearVault();
    window.addEventListener("pagehide", forget);
    return () => window.removeEventListener("pagehide", forget);
  }, []);

  const reopen = useCallback(() => {
    if (!vault.segment) {
      setFailure("invalid");
      setPhase("dead");
      return;
    }
    void exchange(vault.segment);
  }, [exchange]);

  const applyView = useCallback((next: ManageViewDto) => {
    setView(next);
    setSuperseded(false);
  }, []);

  const openGate = useCallback((scope: ManageElevationScope) => {
    /* A rerender or a second refused request must join the verification already
       on screen. Replacing its resolver strands the first caller forever. */
    if (gate.current) return gate.current.promise;

    let resolveGate!: () => void;
    let rejectGate!: (error: unknown) => void;
    const promise = new Promise<void>((resolve, reject) => {
      resolveGate = resolve;
      rejectGate = reject;
    });
    gate.current = { promise, resolve: resolveGate, reject: rejectGate };
    setStepUp({
      scope,
      maskedTarget: "",
      channel: null,
      retryAfterSeconds: 0,
      sending: false,
      errorKey: null,
    });
    return promise;
  }, []);

  const run = useCallback(
    async <T,>(fn: (credentials: Credentials) => Promise<T>): Promise<T> => {
      const attempt = async (): Promise<T> => fn(credentials());
      try {
        return await attempt();
      } catch (error) {
        if (!isApiError(error)) throw error;
        if (error.code === "sessionExpired" || error.code === "sessionRequired") {
          // Not a dead link: the generation rotated (or the 30-minute session
          // ran out). See the header note — this is the compensating path for
          // the unreadable `X-Manage-Session-Refresh`.
          vault.session = "";
          vault.elevation = "";
          vault.scopes = [];
          setElevated(false);
          setScopes([]);
          setPhase("stale");
          throw new SessionLost();
        }
        if (error.code !== "verificationRequired") throw error;
        // The SERVER names the scope; the page never assumes one. A refusal
        // without a scope detail still runs the step-up — the elevation the mock
        // and the worker mint covers every scope, so the retry is what decides.
        const named = (error.details as { scope?: unknown } | undefined)?.scope;
        const scope = (typeof named === "string" ? named : "pii:read") as ManageElevationScope;
        // A booking change invalidates elevation; drop what we hold so the retry
        // cannot send a token the server has already refused.
        vault.elevation = "";
        vault.scopes = [];
        setElevated(false);
        setScopes([]);
        await openGate(scope);
        return attempt();
      }
    },
    [openGate],
  );

  const requestCode = useCallback(async (channel?: "email" | "sms") => {
    setStepUp((current) => (current ? { ...current, sending: true, errorKey: null } : current));
    try {
      const response = await manageSurface.requestManageVerification(
        credentials(),
        channel ? { channel } : {},
      );
      const data = response.data;
      setStepUp((current) =>
        current
          ? {
            ...current,
            sending: false,
            channel: data.channel,
            // Only ever the masked target. The page has not proven who this is.
            maskedTarget: data.masked_target,
            retryAfterSeconds: data.retry_after_seconds,
            errorKey: null,
          }
          : current,
      );
    } catch (error) {
      const code = isApiError(error) ? error.code : "";
      setStepUp((current) =>
        current
          ? {
            ...current,
            sending: false,
            errorKey:
              code === "verificationUnavailable"
                ? "verify.unavailable"
                : code === "otpExhausted"
                  ? "verify.exhausted"
                  : "error.generic",
          }
          : current,
      );
    }
  }, []);

  const submitCode = useCallback(async (code: string) => {
    setStepUp((current) => (current ? { ...current, sending: true, errorKey: null } : current));
    try {
      const response = await manageSurface.confirmManageVerification(credentials(), { code });
      vault.elevation = response.data.elevated_token;
      vault.scopes = response.data.scopes;
      setElevated(true);
      setScopes(response.data.scopes);
      setStepUp(null);
      gate.current?.resolve();
      gate.current = null;
    } catch (error) {
      const apiCode = isApiError(error) ? error.code : "";
      setStepUp((current) =>
        current
          ? {
            ...current,
            sending: false,
            errorKey: apiCode === "otpExhausted" ? "verify.exhausted" : "verify.error",
          }
          : current,
      );
    }
  }, []);

  const abandonStepUp = useCallback(() => {
    setStepUp(null);
    gate.current?.reject(new StepUpAbandoned());
    gate.current = null;
  }, []);

  const reload = useCallback(async () => {
    try {
      const response = await manageSurface.getManageView(credentials());
      setView(response.data);
    } catch (error) {
      if (isApiError(error) && (error.code === "sessionExpired" || error.code === "sessionRequired")) {
        setPhase("stale");
        return;
      }
      throw error;
    }
  }, []);

  const revealFull = useCallback(async () => {
    const response = await run((c) => manageSurface.getManageViewFull(c));
    setView(response.data);
  }, [run]);

  return {
    phase,
    failure,
    view,
    superseded,
    elevated,
    scopes,
    stepUp,
    reopen,
    applyView,
    reload,
    run,
    requestCode,
    submitCode,
    abandonStepUp,
    revealFull,
  };
}

/* ── mutation envelopes ──────────────────────────────────────────────── */

/**
 * ONE key per user INTENT, minted when the intent BEGINS — the moment the
 * confirm dialog opens, not the moment the button is tapped — and reused by
 * every attempt of that intent, including the one the visitor causes by tapping
 * twice. That is the whole of acceptance item 7, which is why each dialog mints
 * its key in a `useMemo` keyed on its own mount rather than in its submit
 * handler.
 *
 * It is deliberately NOT re-minted after a `409 bookingVersionConflict`: the
 * re-confirm is the same intent, and the server stored nothing for the key when
 * it refused, so the second attempt is the first one that can succeed.
 */

/** `view.version`, echoed verbatim. Never re-derived, never defaulted. */
export function envelopeOf(view: ManageViewDto, key: string) {
  return { idempotency_key: key, version: view.version };
}
